'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';

/**
 * "Live now" rail — fetches the {@code live} source from MediaSource
 * unless the operator supplied a manual {@code items} list.
 */
export interface LiveItemInput {
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  href?: string;
  startedAt?: string;
  viewerCount?: number;
  tag?: string;
}

export interface LiveNowStripProps {
  title?: string;
  items?: LiveItemInput[];
  layout?: 'rail' | 'grid';
  showViewerCount?: boolean;
  showStartedAgo?: boolean;
  kicker?: string;
  videoHrefTemplate?: string;
}

function timeAgo(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('liveNow.time.justNow');
    if (mins < 60) return t('liveNow.time.minutes', { minutes: mins });
    const hrs = Math.floor(mins / 60);
    return t('liveNow.time.hours', { hours: hrs });
  } catch {
    return iso;
  }
}

function compactCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function LiveNowStrip({
  title,
  items: manualItems,
  layout = 'rail',
  showViewerCount = true,
  showStartedAgo = true,
  kicker,
  videoHrefTemplate = '/tube/{slug}',
}: LiveNowStripProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('liveNow.title');
  const media = useMediaSource();
  const manual = Array.isArray(manualItems) && manualItems.length > 0;

  const state = useAsyncResource<MediaItem[]>(
    async (signal) => {
      if (manual) return null;
      if (!media) return null;
      const res = await media.videos.fetchSource({ source: 'live', limit: 12, signal });
      return res && res.items.length > 0 ? res.items : null;
    },
    [media, manual],
  );

  if (!manual && !media) {
    return (
      <SectionShell title={heading}>
        <AdapterPlaceholder blockType="LiveNowStrip" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  const data: LiveItemInput[] | undefined = manual
    ? manualItems
    : state.kind === 'loaded'
      ? state.data.map((m) => ({
          title: m.title ?? m.slug,
          imageUrl: m.imageUrl,
          href: videoHrefTemplate.replace('{slug}', encodeURIComponent(m.slug)),
        }))
      : undefined;

  if (!manual && state.kind === 'loading') {
    return (
      <SectionShell title={heading}>
        <SkeletonRow count={6} layout={layout} />
      </SectionShell>
    );
  }

  if (!data || data.length === 0) {
    return (
      <SectionShell title={heading}>
        <EmptyState title={t('liveNow.empty.title')} hint={t('liveNow.empty.hint')} />
      </SectionShell>
    );
  }

  const renderTile = (item: LiveItemInput, i: number) => (
    <Card asChild key={i}>
      <Link
        href={item.href ?? '#'}
        style={{
          ...cardReset,
          flex: layout === 'rail' ? '0 0 280px' : undefined,
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 'var(--radius-md, 8px)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          aria-hidden
          style={{
            aspectRatio: '16 / 9',
            background: item.imageUrl
              ? `url(${item.imageUrl}) center/cover no-repeat`
              : 'var(--color-neutral-200, #e4e4e7)',
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'var(--color-error-600, #dc2626)',
            color: 'var(--color-text-on-brand, #fff)',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: 'var(--font-tracking-wider, 0.06em)',
            textTransform: 'uppercase',
            padding: 'var(--space-1, 4px) var(--space-2, 8px)',
            borderRadius: 4,
          }}
        >
          {t('liveNow.badge')}
        </span>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {kicker && (
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                textTransform: 'uppercase',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {kicker}
            </span>
          )}
          <div style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600, lineHeight: 1.3 }}>
            {item.title}
          </div>
          {item.subtitle && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {item.subtitle}
            </div>
          )}
          {(showViewerCount && typeof item.viewerCount === 'number') ||
          (showStartedAgo && item.startedAt) ? (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {showViewerCount && typeof item.viewerCount === 'number' && (
                <span>{t('liveNow.watching', { viewers: compactCount(item.viewerCount) })}</span>
              )}
              {showViewerCount &&
                typeof item.viewerCount === 'number' &&
                showStartedAgo &&
                item.startedAt &&
                ' · '}
              {showStartedAgo && item.startedAt && (
                <span>{t('liveNow.startedAgo', { time: timeAgo(item.startedAt, t) })}</span>
              )}
            </div>
          ) : null}
        </div>
      </Link>
    </Card>
  );

  return (
    <SectionShell title={heading}>
      {layout === 'grid' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
            gap: 'clamp(12px, 2vw, 16px)',
          }}
        >
          {data.map(renderTile)}
        </div>
      ) : (
        <ContentRail ariaLabel={heading} snap="start">
          {data.map(renderTile)}
        </ContentRail>
      )}
    </SectionShell>
  );
}
